// modules
import React, { Suspense, useEffect, useRef } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

// components
import ModalsContainer from '../components/layout/ModalsContainer';
import AccountLayout from '../components/layout/AccountLayout';
import { ProtectedApp } from '../components/molecules/ProtectedApp';
import ProtectedRoute from '../components/molecules/ProtectedRoute';
import { AdminGate } from '../components/molecules/AdminGate';
import NavigationContext from '../~reusables/contexts/NavigationContext';

// logic
import { TypedRoute } from '../components/Router';
import { $globalLoading, useStore } from '../store';
import { TeamsFeature } from '../components/layout/TeamsFeature';
import {
  AccessTokensPage,
  AccountHomePage,
  AppLevelRouter,
  Authenticate,
  AuthenticateDesktopApp,
  BillingPage,
  CreateTeamPage,
  DesktopCheckoutRedirect,
  InvitePage,
  LegacySettingsRouter,
  prefetchPage,
  prefetchRoutes,
  ProfilePage,
  ReleaseRouter,
  SecurityTokensPage,
  SigningPage,
  SuperAdminLevelRouter,
  TeamGeneralPage,
  TeamSetupPage,
  TransferPage,
  UserManagementPage,
  V2GitHubCallback,
} from './routes/lazyRoutes';

// Account-level routes with new sidebar design
const accountLevelPaths = [
  '/',
  '/settings/profile',
  '/settings/sign-in-methods',
  '/settings/security-tokens',
  '/settings/access-tokens',
  '/settings/billing',
  '/settings/users',
  '/settings/signing',
  '/settings/team',
];

const AccountLevelRouter: React.FC<RouteComponentProps> = (routeProps) => {
  return (
    <AccountLayout>
      <Switch>
        <Route
          exact
          path="/"
          render={(props) => <AccountHomePage {...props} />}
        />
        <Route exact path="/settings/profile" render={() => <ProfilePage />} />
        <Route
          exact
          path="/settings/sign-in-methods"
          render={() => <Redirect to="/settings/profile" />}
        />
        <Route
          exact
          path="/settings/security-tokens"
          render={() => <SecurityTokensPage />}
        />
        <Route
          exact
          path="/settings/access-tokens"
          render={() => <AccessTokensPage />}
        />
        <Route exact path="/settings/billing" render={() => <BillingPage />} />
        <Route
          exact
          path="/settings/users"
          render={() => <UserManagementPage />}
        />
        <Route
          exact
          path="/settings/signing"
          // Signing identities are for v2 apps; the page has its own gate,
          // as a team's signing managers pass on the team's flag.
          render={() => <SigningPage />}
        />
        <Route exact path="/settings/team" render={() => <TeamGeneralPage />} />
      </Switch>
      <ModalsContainer {...routeProps} />
    </AccountLayout>
  );
};

const LegacyAccountRedirect: React.FC<RouteComponentProps> = ({ location }) => {
  const searchParams = new URLSearchParams(location.search);

  if (searchParams.get('tab') === 'access-tokens') {
    return <Redirect to="/settings/access-tokens" />;
  }

  const inviteAccountId = searchParams.get('id');
  if (inviteAccountId) {
    return <Redirect to={`/invites/${encodeURIComponent(inviteAccountId)}`} />;
  }

  return <Redirect to="/settings/users" />;
};

const superAdminPaths = [
  '/super-admin',
  '/super-admin/customers',
  '/super-admin/customers/:ownerId/:appId',
  '/super-admin/inbox',
  '/super-admin/builds',
  '/super-admin/feature-flags',
];

const TeamLevelRouter: React.FC = () => {
  return (
    <Switch>
      <TypedRoute
        path={['/apps/:id/builds/:buildId/release']}
        render={(routeProps) => (
          <ProtectedApp {...routeProps} component={ReleaseRouter} />
        )}
      />

      {/* New account-level routes with sidebar layout */}
      <Route
        exact
        path={accountLevelPaths}
        render={(routeProps) => <AccountLevelRouter {...routeProps} />}
      />

      {/* Super Admin routes with new sidebar layout */}
      <Route
        exact
        path={superAdminPaths}
        render={(routeProps) => (
          <AdminGate>
            <SuperAdminLevelRouter {...routeProps} />
          </AdminGate>
        )}
      />

      <Route exact path="/account" component={LegacyAccountRedirect} />

      {/* GitHub's OAuth redirect for v2 apps' CI */}
      <Route exact path="/v2/github/callback" component={V2GitHubCallback} />

      <Route
        exact
        path="/transfers/:appId"
        render={(routeProps) => (
          <>
            <TransferPage />
            <ModalsContainer {...routeProps} />
          </>
        )}
      />

      <Route
        exact
        path="/teams/new"
        render={(routeProps) => (
          <TeamsFeature>
            <CreateTeamPage />
            <ModalsContainer {...routeProps} />
          </TeamsFeature>
        )}
      />

      <Route
        exact
        path="/invites/:accountId"
        render={(routeProps) => (
          <>
            <InvitePage />
            <ModalsContainer {...routeProps} />
          </>
        )}
      />

      <Route
        exact
        path="/team-setup"
        render={(routeProps) => (
          <TeamsFeature>
            <TeamSetupPage />
            <ModalsContainer {...routeProps} />
          </TeamsFeature>
        )}
      />

      {/* App-level routes with new sidebar layout */}
      <Route
        path="/apps/:id"
        render={(routeProps) => (
          <ProtectedApp {...routeProps} component={AppLevelRouter} />
        )}
      />

      {/* Legacy routes with old header */}
      <Route
        exact
        path="/settings"
        render={(routeProps) => <LegacySettingsRouter {...routeProps} />}
      />
      <Redirect to="/" />
    </Switch>
  );
};

const WebAppV2: React.FC = () => {
  // Prefetch the other route chunks once the first page has its data, so they
  // don't compete with it for bandwidth and the main thread
  const globalLoading = useStore($globalLoading);
  const hasPrefetched = useRef(false);
  useEffect(() => {
    prefetchPage(window.location.pathname);
  }, []);
  useEffect(() => {
    if (globalLoading || hasPrefetched.current) return;
    hasPrefetched.current = true;
    prefetchRoutes();
  }, [globalLoading]);

  return (
    <NavigationContext>
      <Suspense>
        <Switch>
          <Route
            exact
            path="/signup"
            render={(routeProps) => (
              <Authenticate type="signup" {...routeProps} />
            )}
          />
          <Route
            exact
            path="/login"
            render={(routeProps) => (
              <Authenticate type="login" {...routeProps} />
            )}
          />
          <Route
            exact
            path="/desktop-app"
            render={(routeProps) => <AuthenticateDesktopApp {...routeProps} />}
          />
          <Route
            exact
            path="/desktop-checkout-redirect/:appId"
            render={(routeProps) => (
              <DesktopCheckoutRedirect appId={routeProps.match.params.appId} />
            )}
          />
          <ProtectedRoute path="/" component={TeamLevelRouter} />
          <Redirect to="/" />
        </Switch>
      </Suspense>
    </NavigationContext>
  );
};

export default WebAppV2;
